/** @graphql agg **/
import { graphql } from 'react-relay';
import {
	createActionsHook,
	createStateHook,
	createStore,
	type StoreActionApi,
} from 'react-sweet-state';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type { PromoDismissed } from '../connector-promo-config-types.ts';

import type {
	useConnectorOnboardingStateQuery$data,
	useConnectorOnboardingStateQuery$variables,
} from './__generated__/useConnectorOnboardingStateQuery.graphql.ts';

export type ConnectorOnboardingState = {
	enrolmentDate: string | undefined;
	spotlightShown: boolean;
	promoDismissed: PromoDismissed | undefined;
};

type ConnectorOnboardingStateUpdate = {
	key: string;
	value: string;
};

export type UseConnectorOnboardingStateResult = {
	onboardingState: ConnectorOnboardingState | undefined;
	loading: boolean;
	error?: Error;
	updateOnboardingState: (state: ConnectorOnboardingStateUpdate) => void;
};

type ConnectorOnboardingStateEntries = NonNullable<
	NonNullable<useConnectorOnboardingStateQuery$data['userPreferences']>['onboardingState']
>;

const EMPTY_ENTRIES: ConnectorOnboardingStateEntries = [];

const connectorOnboardingStateQuery = graphql`
	query useConnectorOnboardingStateQuery {
		userPreferences {
			onboardingState(
				key: ["twcg-1489:enrolmentDate", "twcg-1489:spotlightShown", "twcg-1489:promoDismissed"]
			) {
				key
				value
			}
		}
	}
`;

// Optimistic local overrides layered on top of the AGG query result, so dismiss
// and spotlight updates apply immediately without waiting for a refetch.
const LocalOnboardingStateStore = createStore({
	initialState: {} as Partial<ConnectorOnboardingState>,
	actions: {
		updateLocalOnboardingState:
			({ key, value }: ConnectorOnboardingStateUpdate) =>
			({ setState }: StoreActionApi<Partial<ConnectorOnboardingState>>): void => {
				switch (key) {
					case TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE:
						setState({ enrolmentDate: value });
						break;
					case TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN:
						setState({ spotlightShown: value === 'true' });
						break;
					case TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED:
						setState({ promoDismissed: value as PromoDismissed });
						break;
				}
			},
	},
	name: 'Twcg1489LocalOnboardingStateStore',
});

const useLocalOnboardingState = createStateHook(LocalOnboardingStateStore);
const useLocalOnboardingStateActions = createActionsHook(LocalOnboardingStateStore);

/**
 * Fetches TWCG-1489 connector onboarding state from userPreferences.onboardingState
 * via AGG. Uses the non-deprecated userPreferences path so the preloaded cache entry
 * matches the hook's request exactly, avoiding a redundant network round-trip.
 */
export function useConnectorOnboardingState({
	skip,
}: { skip?: boolean } = {}): UseConnectorOnboardingStateResult {
	const isLicensed = useIsLicensed();

	const { data, loading, error } = useQueryInterop<
		useConnectorOnboardingStateQuery$data,
		useConnectorOnboardingStateQuery$variables
	>(connectorOnboardingStateQuery, {
		variables: {},
		skip: skip || !isLicensed,
		interopApolloAggClient: true,
		fetchPolicy: 'cache-first',
	});

	const entries = data?.userPreferences?.onboardingState ?? EMPTY_ENTRIES;
	const localOnboardingStateSnapshot = useLocalOnboardingState();
	const { updateLocalOnboardingState: updateOnboardingState } = useLocalOnboardingStateActions();

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
		return { onboardingState: undefined, loading: false, error, updateOnboardingState };
	}

	if (loading) {
		return { onboardingState: undefined, loading: true, updateOnboardingState };
	}

	const findValue = (key: string): string | undefined =>
		entries.find((e) => e.key === key)?.value ?? undefined;

	const queryOnboardingState: ConnectorOnboardingState = {
		enrolmentDate: findValue(TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE),
		spotlightShown: findValue(TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN) === 'true',
		promoDismissed: findValue(TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED) as
			| PromoDismissed
			| undefined,
	};

	return {
		onboardingState: {
			...queryOnboardingState,
			...localOnboardingStateSnapshot,
		},
		loading: false,
		updateOnboardingState,
	};
}
